'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowRight, ArrowLeft, CheckCircle2, ChevronRight } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/app/components/PublicFooter';

const WEBSITE_CATEGORIES = [
  'Hotel / Resort', 'Restaurant', 'Café / Coffee Shop', 'Fast Food',
  'Sweet Shop / Mithai', 'Bakery', 'Grocery Store', 'General Store',
  'Supermarket / Mart', 'Retail Store', 'Clothing Store / Boutique',
  'Jewellery Store', 'Footwear Store', 'Cosmetics / Beauty Store',
  'Mobile / Electronics Store', 'Furniture Store', 'Hardware Store',
  'Automobile / Car Dealer', 'Bike Dealer', 'Repair / Service Centre',
  'Salon / Beauty Parlour', 'Spa', 'Gym / Fitness Centre', 'School',
  'College', 'Coaching Institute', 'Educational Institute', 'Hospital',
  'Clinic', 'Pharmacy', 'Diagnostic Centre', 'Lawyer / Legal Services',
  'CA / Accounting / Professional Services', 'Consultancy', 'Real Estate',
  'Construction', 'Architecture / Interior Design', 'Photography / Videography',
  'Event Management', 'Travel / Tourism', 'Transport / Logistics',
  'Manufacturing / Factory', 'Agriculture / Farming Business',
  'NGO / Trust / Organization', 'Religious Organization',
  'E-commerce / Online Store', 'Corporate / Company', 'Personal Portfolio',
  'Freelancer / Creator', 'Blog / Magazine / News', 'Restaurant + Hotel', 'Other',
];

const CONTACT_METHODS = ['WhatsApp', 'Phone Call', 'Email'];

interface Step1Data {
  website_category: string;
  website_requirements: string;
  page_count: string;
  has_domain: boolean;
  has_hosting: boolean;
  has_logo: boolean;
  has_content: boolean;
  additional_requirements: string;
}

interface Step2Data {
  customer_name: string;
  business_name: string;
  phone: string;
  whatsapp: string;
  email: string;
  city: string;
  state: string;
  preferred_contact_method: string;
  preferred_contact_time: string;
  existing_website: string;
  instagram_url: string;
  facebook_url: string;
  additional_url: string;
  consent_given: boolean;
}

const defaultStep1: Step1Data = {
  website_category: '',
  website_requirements: '',
  page_count: '',
  has_domain: false,
  has_hosting: false,
  has_logo: false,
  has_content: false,
  additional_requirements: '',
};

const defaultStep2: Step2Data = {
  customer_name: '',
  business_name: '',
  phone: '',
  whatsapp: '',
  email: '',
  city: '',
  state: '',
  preferred_contact_method: 'WhatsApp',
  preferred_contact_time: '',
  existing_website: '',
  instagram_url: '',
  facebook_url: '',
  additional_url: '',
  consent_given: false,
};

export default function GetWebsitePage() {
  const [step, setStep] = useState<1 | 2 | 'success'>(1);
  const [step1, setStep1] = useState<Step1Data>(defaultStep1);
  const [step2, setStep2] = useState<Step2Data>(defaultStep2);
  const [errors1, setErrors1] = useState<Partial<Record<keyof Step1Data, string>>>({});
  const [errors2, setErrors2] = useState<Partial<Record<keyof Step2Data, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [enquiryNumber, setEnquiryNumber] = useState('');

  const validateStep1 = () => {
    const e: typeof errors1 = {};
    if (!step1.website_category) e.website_category = 'Please select a website category.';
    if (!step1.website_requirements.trim() || step1.website_requirements.trim().length < 20)
      e.website_requirements = 'Please describe your requirements (at least 20 characters).';
    setErrors1(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e: typeof errors2 = {};
    if (!step2.customer_name.trim()) e.customer_name = 'Full name is required.';
    if (!step2.business_name.trim()) e.business_name = 'Business name is required.';
    if (!step2.phone.trim() || !/^[6-9]\d{9}$/.test(step2.phone.replace(/\s/g, '')))
      e.phone = 'Enter a valid 10-digit Indian mobile number.';
    if (!step2.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(step2.email))
      e.email = 'Enter a valid email address.';
    if (!step2.city.trim()) e.city = 'City is required.';
    if (!step2.consent_given) e.consent_given = 'You must agree to be contacted.';
    if (step2.whatsapp && !/^[6-9]\d{9}$/.test(step2.whatsapp.replace(/\s/g, '')))
      e.whatsapp = 'Enter a valid 10-digit WhatsApp number.';
    setErrors2(e);
    return Object.keys(e).length === 0;
  };

  const handleContinue = () => {
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const supabase = createClient();
      const payload = {
        enquiry_number: '',
        website_category: step1.website_category,
        website_requirements: step1.website_requirements,
        page_count: step1.page_count || null,
        has_domain: step1.has_domain,
        has_hosting: step1.has_hosting,
        has_logo: step1.has_logo,
        has_content: step1.has_content,
        additional_requirements: step1.additional_requirements || null,
        customer_name: step2.customer_name,
        business_name: step2.business_name,
        phone: step2.phone,
        whatsapp: step2.whatsapp || null,
        email: step2.email,
        city: step2.city,
        state: step2.state || null,
        preferred_contact_method: step2.preferred_contact_method,
        preferred_contact_time: step2.preferred_contact_time || null,
        existing_website: step2.existing_website || null,
        instagram_url: step2.instagram_url || null,
        facebook_url: step2.facebook_url || null,
        additional_url: step2.additional_url || null,
        consent_given: step2.consent_given,
        status: 'New' as const,
      };
      const { data, error } = await supabase
        .from('website_enquiries')
        .insert(payload)
        .select('enquiry_number')
        .single();
      if (error) throw new Error(error.message);
      setEnquiryNumber(data?.enquiry_number || '');
      setStep('success');
    } catch (err: any) {
      setSubmitError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappUrl = `https://wa.me/919001612303?text=${encodeURIComponent('Hi, I just submitted a website enquiry. My enquiry ID is ' + enquiryNumber)}`;

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />
      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">

          {/* Success Screen */}
          {step === 'success' && (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-emerald/10 border border-emerald/30 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 size={40} className="text-emerald" />
              </div>
              <h1 className="font-display font-bold text-3xl text-foreground mb-3">
                Your Website Enquiry Has Been Submitted
              </h1>
              <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-xl px-5 py-3 mb-6">
                <span className="text-sm text-muted-foreground font-medium">Enquiry ID:</span>
                <span className="font-mono-code font-bold text-primary text-lg">{enquiryNumber}</span>
              </div>
              <p className="text-muted-foreground max-w-md mx-auto mb-8 leading-relaxed">
                We have received your requirements. Our team will review your enquiry and contact you shortly via your preferred contact method.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link href="/" className="btn-outline text-sm px-6 py-3">
                  Back to Webmint
                </Link>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-sm px-6 py-3"
                  style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                >
                  Contact on WhatsApp
                  <ArrowRight size={14} />
                </a>
              </div>
            </div>
          )}

          {/* Step 1 */}
          {step === 1 && (
            <div>
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">1</span>
                    <span className="text-sm font-semibold text-foreground">Website Requirements</span>
                  </div>
                  <ChevronRight size={14} className="text-muted-foreground" />
                  <div className="flex items-center gap-2 opacity-40">
                    <span className="w-7 h-7 rounded-full bg-muted text-muted-foreground text-xs font-bold flex items-center justify-center">2</span>
                    <span className="text-sm font-medium text-muted-foreground">Contact Details</span>
                  </div>
                </div>
                <div className="h-1 bg-muted rounded-full mt-3">
                  <div className="h-1 bg-primary rounded-full w-1/2 transition-all duration-300" />
                </div>
              </div>

              <div className="glass-card rounded-2xl border border-border/40 p-6 sm:p-8">
                <h1 className="font-display font-bold text-2xl text-foreground mb-1">Tell Us About Your Website</h1>
                <p className="text-muted-foreground text-sm mb-8">Select your business type and describe what you need.</p>

                {/* Category Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-foreground mb-3">
                    What type of website do you need? <span className="text-rose">*</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1 scrollbar-thin">
                    {WEBSITE_CATEGORIES.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => { setStep1(s => ({ ...s, website_category: cat })); setErrors1(e => ({ ...e, website_category: '' })); }}
                        className={`text-left px-3 py-2.5 rounded-xl border text-xs font-medium transition-all duration-150 ${
                          step1.website_category === cat
                            ? 'border-primary bg-primary/10 text-primary' :'border-border/40 text-muted-foreground hover:border-primary/40 hover:text-foreground'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                  {errors1.website_category && <p className="text-rose text-xs mt-1.5">{errors1.website_category}</p>}
                </div>

                {/* Requirements */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    What do you want your website to do? <span className="text-rose">*</span>
                  </label>
                  <textarea
                    rows={5}
                    value={step1.website_requirements}
                    onChange={e => { setStep1(s => ({ ...s, website_requirements: e.target.value })); setErrors1(e2 => ({ ...e2, website_requirements: '' })); }}
                    placeholder="Tell us what kind of website you need, what services/products you offer, what pages you want, whether you need online booking, WhatsApp integration, online payments, a menu, gallery, contact form, etc."
                    className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 resize-none"
                  />
                  {errors1.website_requirements && <p className="text-rose text-xs mt-1">{errors1.website_requirements}</p>}
                </div>

                {/* Optional fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Approximate number of pages</label>
                    <input
                      type="text"
                      value={step1.page_count}
                      onChange={e => setStep1(s => ({ ...s, page_count: e.target.value }))}
                      placeholder="e.g. 5–10 pages"
                      className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Additional requirements</label>
                    <input
                      type="text"
                      value={step1.additional_requirements}
                      onChange={e => setStep1(s => ({ ...s, additional_requirements: e.target.value }))}
                      placeholder="Any other specific needs"
                      className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60"
                    />
                  </div>
                </div>

                {/* Checkboxes */}
                <div className="mb-8">
                  <label className="block text-xs font-semibold text-muted-foreground mb-3">What do you already have?</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'has_domain', label: 'Domain name' },
                      { key: 'has_hosting', label: 'Web hosting' },
                      { key: 'has_logo', label: 'Logo / branding' },
                      { key: 'has_content', label: 'Website images / content' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2.5 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={step1[key as keyof Step1Data] as boolean}
                          onChange={e => setStep1(s => ({ ...s, [key]: e.target.checked }))}
                          className="w-4 h-4 rounded border-border/60 accent-primary"
                        />
                        <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <button onClick={handleContinue} className="btn-primary w-full justify-center text-sm py-3">
                  Continue
                  <ArrowRight size={15} />
                </button>
              </div>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div>
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex items-center gap-2 opacity-40">
                    <span className="w-7 h-7 rounded-full bg-emerald text-white text-xs font-bold flex items-center justify-center">✓</span>
                    <span className="text-sm font-medium text-muted-foreground">Website Requirements</span>
                  </div>
                  <ChevronRight size={14} className="text-muted-foreground" />
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">2</span>
                    <span className="text-sm font-semibold text-foreground">Contact Details</span>
                  </div>
                </div>
                <div className="h-1 bg-muted rounded-full mt-3">
                  <div className="h-1 bg-primary rounded-full w-full transition-all duration-300" />
                </div>
              </div>

              <div className="glass-card rounded-2xl border border-border/40 p-6 sm:p-8">
                <h2 className="font-display font-bold text-2xl text-foreground mb-1">Tell Us How We Can Contact You</h2>
                <p className="text-muted-foreground text-sm mb-8">We'll use this to get back to you about your website.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Full Name <span className="text-rose">*</span></label>
                    <input type="text" value={step2.customer_name} onChange={e => { setStep2(s => ({ ...s, customer_name: e.target.value })); setErrors2(e2 => ({ ...e2, customer_name: '' })); }}
                      placeholder="Your full name" className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60" />
                    {errors2.customer_name && <p className="text-rose text-xs mt-1">{errors2.customer_name}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Business / Organization Name <span className="text-rose">*</span></label>
                    <input type="text" value={step2.business_name} onChange={e => { setStep2(s => ({ ...s, business_name: e.target.value })); setErrors2(e2 => ({ ...e2, business_name: '' })); }}
                      placeholder="Your business name" className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60" />
                    {errors2.business_name && <p className="text-rose text-xs mt-1">{errors2.business_name}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Mobile Number <span className="text-rose">*</span></label>
                    <input type="tel" value={step2.phone} onChange={e => { setStep2(s => ({ ...s, phone: e.target.value })); setErrors2(e2 => ({ ...e2, phone: '' })); }}
                      placeholder="10-digit mobile number" className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60" />
                    {errors2.phone && <p className="text-rose text-xs mt-1">{errors2.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">WhatsApp Number <span className="text-muted-foreground font-normal">(optional)</span></label>
                    <input type="tel" value={step2.whatsapp} onChange={e => { setStep2(s => ({ ...s, whatsapp: e.target.value })); setErrors2(e2 => ({ ...e2, whatsapp: '' })); }}
                      placeholder="If different from mobile" className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60" />
                    {errors2.whatsapp && <p className="text-rose text-xs mt-1">{errors2.whatsapp}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Email Address <span className="text-rose">*</span></label>
                    <input type="email" value={step2.email} onChange={e => { setStep2(s => ({ ...s, email: e.target.value })); setErrors2(e2 => ({ ...e2, email: '' })); }}
                      placeholder="your@email.com" className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60" />
                    {errors2.email && <p className="text-rose text-xs mt-1">{errors2.email}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">City <span className="text-rose">*</span></label>
                    <input type="text" value={step2.city} onChange={e => { setStep2(s => ({ ...s, city: e.target.value })); setErrors2(e2 => ({ ...e2, city: '' })); }}
                      placeholder="Your city" className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60" />
                    {errors2.city && <p className="text-rose text-xs mt-1">{errors2.city}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">State <span className="text-muted-foreground font-normal">(optional)</span></label>
                    <input type="text" value={step2.state} onChange={e => setStep2(s => ({ ...s, state: e.target.value }))}
                      placeholder="Your state" className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Preferred Contact Time <span className="text-muted-foreground font-normal">(optional)</span></label>
                    <input type="text" value={step2.preferred_contact_time} onChange={e => setStep2(s => ({ ...s, preferred_contact_time: e.target.value }))}
                      placeholder="e.g. Weekdays 10am–6pm" className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60" />
                  </div>
                </div>

                {/* Preferred Contact Method */}
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-foreground mb-2">Preferred Contact Method</label>
                  <div className="flex gap-2 flex-wrap">
                    {CONTACT_METHODS.map(m => (
                      <button key={m} type="button" onClick={() => setStep2(s => ({ ...s, preferred_contact_method: m }))}
                        className={`px-4 py-2 rounded-xl border text-xs font-medium transition-all duration-150 ${step2.preferred_contact_method === m ? 'border-primary bg-primary/10 text-primary' : 'border-border/40 text-muted-foreground hover:border-primary/40'}`}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>

                {/* URLs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Existing Website URL <span className="text-muted-foreground font-normal">(optional)</span></label>
                    <input type="url" value={step2.existing_website} onChange={e => setStep2(s => ({ ...s, existing_website: e.target.value }))}
                      placeholder="https://yourwebsite.com" className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Instagram URL <span className="text-muted-foreground font-normal">(optional)</span></label>
                    <input type="url" value={step2.instagram_url} onChange={e => setStep2(s => ({ ...s, instagram_url: e.target.value }))}
                      placeholder="https://instagram.com/yourbusiness" className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Facebook URL <span className="text-muted-foreground font-normal">(optional)</span></label>
                    <input type="url" value={step2.facebook_url} onChange={e => setStep2(s => ({ ...s, facebook_url: e.target.value }))}
                      placeholder="https://facebook.com/yourbusiness" className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Other Social / Profile URL <span className="text-muted-foreground font-normal">(optional)</span></label>
                    <input type="url" value={step2.additional_url} onChange={e => setStep2(s => ({ ...s, additional_url: e.target.value }))}
                      placeholder="LinkedIn, YouTube, etc." className="w-full bg-muted/30 border border-border/40 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60" />
                  </div>
                </div>

                {/* Consent */}
                <div className="mb-6">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input type="checkbox" checked={step2.consent_given} onChange={e => { setStep2(s => ({ ...s, consent_given: e.target.checked })); setErrors2(e2 => ({ ...e2, consent_given: '' })); }}
                      className="w-4 h-4 mt-0.5 rounded border-border/60 accent-primary flex-shrink-0" />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                      I agree that Webmint may contact me regarding this website enquiry. <span className="text-rose">*</span>
                    </span>
                  </label>
                  {errors2.consent_given && <p className="text-rose text-xs mt-1 ml-7">{errors2.consent_given}</p>}
                </div>

                {submitError && (
                  <div className="mb-4 p-3 rounded-xl bg-rose/10 border border-rose/30 text-rose text-sm">{submitError}</div>
                )}

                <div className="flex gap-3">
                  <button onClick={() => setStep(1)} className="btn-outline text-sm px-5 py-3 flex items-center gap-2">
                    <ArrowLeft size={14} /> Back
                  </button>
                  <button onClick={handleSubmit} disabled={submitting} className="btn-primary flex-1 justify-center text-sm py-3 disabled:opacity-60 disabled:cursor-not-allowed">
                    {submitting ? (
                      <span className="flex items-center gap-2"><span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />Submitting...</span>
                    ) : (
                      <span className="flex items-center gap-2">Submit Enquiry <ArrowRight size={14} /></span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
